import React, { useMemo } from 'react'
import { cn, TextButton } from '@yanolja-rnd/nol-design-system'
import { IcError, IcInfo } from '@yanolja-rnd/nol-icons'
import InfoListBottomSheet from '~/app/components-nds/common/info-list-bottom-sheet'
import useModalHash from '~/app/components/common/useModalHash'
import type { CancellationPolicyClient, CancellationPolicyType } from './types'

/**
 * 취소규정 타입에 따른 배경색을 반환하는 함수
 */
const getBgColor = (type: CancellationPolicyType) => {
  switch (type) {
    case 'free':
    case 'free-until':
      return 'bg-fill-success-weak'
    case 'fee-charged':
    case 'policy-error':
      return 'bg-fill-warning-weak'
    case 'non-refundable-with-cancel':
    case 'non-refundable':
      return 'bg-fill-danger-weak'
  }
}

/**
 * 취소규정 타입에 따른 텍스트 색상을 반환하는 함수
 */
const getTextColor = (type: CancellationPolicyType) => {
  switch (type) {
    case 'free':
    case 'free-until':
      return 'text-text-success-main'
    case 'fee-charged':
    case 'policy-error':
      return 'text-text-warning-main'
    case 'non-refundable-with-cancel':
    case 'non-refundable':
      return 'text-text-danger-main'
  }
}

/**
 * 취소규정 배지의 내용을 렌더링하는 함수
 */
const renderContent = (
  props: CancellationPolicyClient,
  setIsBottomSheetOpen: (isOpen: boolean) => void,
  showPolicyInfo: boolean
) => {
  const { type } = props

  switch (type) {
    case 'free':
      return (
        <>
          <span className="typography-body-12-bold">무료취소</span>
          {showPolicyInfo && (
            <IcInfo
              className="flex-shrink-0 cursor-pointer"
              height={16}
              width={16}
              onClick={() => setIsBottomSheetOpen(true)}
            />
          )}
        </>
      )

    case 'free-until':
      return (
        <>
          <span className="typography-body-12-regular">{`${props.freeUntilDate} 전까지`}</span>
          <span className="typography-body-12-bold">무료취소</span>
          {showPolicyInfo && (
            <IcInfo
              className="flex-shrink-0 cursor-pointer"
              height={16}
              width={16}
              onClick={() => setIsBottomSheetOpen(true)}
            />
          )}
        </>
      )

    case 'fee-charged':
      return (
        <>
          <span className="typography-body-12-regular">{`${props.feeUntilDate} 전까지`}</span>
          <span className="typography-body-12-bold">{props.feeText}</span>
          {showPolicyInfo && (
            <IcInfo
              className="flex-shrink-0 cursor-pointer"
              height={16}
              width={16}
              onClick={() => setIsBottomSheetOpen(true)}
            />
          )}
        </>
      )

    case 'non-refundable-with-cancel':
      return <span className="typography-body-12-bold">취소 및 환불불가</span>

    case 'non-refundable':
      return <span className="typography-body-12-bold">환불불가</span>

    case 'policy-error':
      return (
        <div className="flex w-full items-center justify-between gap-8">
          <div className="flex gap-2">
            <IcError className="flex-shrink-0" height={16} width={16} />
            <p className="flex items-center typography-body-12-bold">
              취소규정 정보를 불러오지 못했어요.
            </p>
          </div>
          <TextButton
            className="flex-shrink-0"
            theme="underline"
            mobileSize="small"
            pcSize="small"
            weight="bold"
            onClick={props.onRefresh}
          >
            새로 고침
          </TextButton>
        </div>
      )

    default:
      return null
  }
}

/**
 * CancellationPolicy Component
 * 장바구니 상품의 취소 규정을 표시하는 배지 컴포넌트
 */
const CancellationPolicy: React.FC<CancellationPolicyClient> = props => {
  const { type } = props
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useModalHash({ id: `CancellationPolicy` })

  const hasPolicyInfo = type === 'fee-charged' || type === 'free' || type === 'free-until'
  const showPolicyInfo = useMemo(() => {
    if (hasPolicyInfo) {
      return !!props.policyInfo && props.policyInfo.length > 0
    }
    return false
  }, [hasPolicyInfo, props])
  return (
    <>
      <div
        className={cn(
          'flex min-h-42 w-full items-center justify-center gap-2 rounded-12 px-16',
          type === 'policy-error' ? 'py-8' : 'py-12',
          getBgColor(type),
          getTextColor(type)
        )}
      >
        {renderContent(props, setIsBottomSheetOpen, showPolicyInfo)}
      </div>

      {hasPolicyInfo && showPolicyInfo && (
        <InfoListBottomSheet
          isOpen={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          header="취소 규정"
          contents={props.policyInfo ?? []}
        />
      )}
    </>
  )
}

export default CancellationPolicy
